import { RouteColors } from './RouteColors';

// Interface for video attached to a route
export interface RouteVideo {
  id: string;
  url: string;
  title?: string;
  uploadedBy: string; // User ID
  uploadedByName?: string; // User display name
  uploadedAt: number; // Timestamp
}

// ClimbingRoute model representing a climbing route/problem
export class ClimbingRoute {
  id: string;
  style?: string;
  grade?: number;
  color?: string; // References the key in RouteColors
  rope?: string;
  setterId?: string;
  setterName?: string;
  note?: string;
  comments?: string;
  date?: number; // Timestamp
  status?: string;
  picture?: string;
  location?: string;
  points?: number; // Points for completing this route
  videos?: RouteVideo[]; // Array of videos for this route

  constructor(
    id: string,
    options: {
      style?: string;
      grade?: number;
      color?: string;
      rope?: string;
      setterId?: string;
      setterName?: string;
      note?: string;
      comments?: string;
      date?: number;
      status?: string;
      picture?: string;
      location?: string;
      points?: number;
      videos?: RouteVideo[];
    } = {}
  ) {
    this.id = id;
    this.style = options.style;
    this.grade = options.grade;
    this.color = options.color;
    this.rope = options.rope;
    this.setterId = options.setterId;
    this.setterName = options.setterName;
    this.note = options.note;
    this.comments = options.comments;
    this.date = options.date;
    this.status = options.status;
    this.picture = options.picture;
    this.location = options.location;
    this.points = options.points;
    this.videos = options.videos;
  }

  // Method to convert to Firestore data
  toFirestore() {
    const storageColor = this.color ? getStorageValueFromColor(this.color) : null;
    
    // Create a clean object with no undefined values
    const cleanData: Record<string, any> = {
      id: this.id,
      style: this.style || null,
      grade: this.grade !== undefined ? this.grade : null,
      color: storageColor,
      rope: this.rope || null,
      setterId: this.setterId || null,
      setterName: this.setterName || null,
      note: this.note || null,
      comments: this.comments || null,
      date: this.date || null,
      status: this.status || null,
      picture: this.picture || null,
      location: this.location || null,
      points: this.points !== undefined ? this.points : null,
      videos: this.videos || []
    };
    
    return cleanData;
  }

  // Static method to create ClimbingRoute from Firestore data
  static fromFirestore(data: any): ClimbingRoute {
    return new ClimbingRoute(data.id, {
      style: data.style,
      grade: data.grade,
      color: data.color ? getColorFromStorageValue(data.color) : undefined,
      rope: data.rope,
      setterId: data.setterId,
      setterName: data.setterName,
      note: data.note,
      comments: data.comments,
      date: data.date,
      status: data.status,
      picture: data.picture,
      location: data.location,
      points: data.points,
      videos: data.videos || []
    });
  }
}

// Helper function to get color value from storage value
export const getColorFromStorageValue = (storageValue?: string): string | undefined => {
  if (!storageValue) return undefined;
  
  
  const entry = Object.entries(RouteColors).find(([_, value]) => value === storageValue);
  
  return entry ? entry[0] : undefined;
};

// Helper function to get storage value from color
export const getStorageValueFromColor = (color?: string): string | undefined => {
  if (!color) return undefined;
  
  const storageValue = RouteColors[color as keyof typeof RouteColors];
  return storageValue;
};
